import { useMemo } from "react";
import {
  PaginatedFilterQuery,
  type CombinedWarehouseOrderItemDto,
} from "@lib/ShiOneClient";
import type { DrawerRouteContainerProps } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { useGetWarehouseOrders } from "@features/assets-feature/hooks/warehouse/useGetWarehouseOrders";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";
import { StackedIntegrationCenterTransferIntegrationDrawer } from "./StackedIntegrationCenterTransferIntegrationDrawer";
import { ReadyDrawerBase } from "./DrawerBaseReadiness";
import { RoutedDrawerLoading } from "./RoutedDrawerLoading";
import {
  matchesIntegrationTransferRouteId,
  parseIntegrationTransferRouteId,
} from "./integrationTransferRouteId";
import { useRouteNotFoundToast } from "shared-ui/src/hooks/routedOverlays/useRouteNotFoundToast";

export const INTEGRATION_TRANSFER_NOT_FOUND_MESSAGE =
  "Integration Center details aren't available for this item.";

const isIntegrationTransferPushProps = (
  value: Record<string, unknown> | undefined,
): value is { details: CombinedWarehouseOrderItemDto } =>
  value?.details != null && typeof value.details === "object";

/**
 * Routed container for `.../integration-transfer/{salesId}~{uniqueIdentifier}`. An in-app
 * open reuses the pushed pick ticket. A deep link or refresh looks the ticket up in the
 * order's warehouse orders, the same query the order drawer runs.
 */
export function RoutedIntegrationTransferDrawer(
  props: DrawerRouteContainerProps,
) {
  const { open, onClose, onCloseAll, zIndex, depth, levelPath, cachedProps } =
    props;
  if (isIntegrationTransferPushProps(cachedProps)) {
    return (
      <ReadyDrawerBase path={open ? levelPath : null}>
        <StackedIntegrationCenterTransferIntegrationDrawer
          open={open}
          onClose={onClose}
          onCloseAll={onCloseAll}
          zIndex={zIndex}
          depth={depth}
          details={cachedProps.details}
        />
      </ReadyDrawerBase>
    );
  }
  return <ColdIntegrationTransferDrawer {...props} />;
}

function ColdIntegrationTransferDrawer({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  routeId,
  levelPath,
  onNotFound,
}: DrawerRouteContainerProps) {
  const target = useMemo(
    () => parseIntegrationTransferRouteId(routeId),
    [routeId],
  );
  const { hasPermission } = useClaims();
  const canReadWarehouseOrders = hasPermission(
    ShiOnePermission.warehouseOrderRead,
  );

  // Same query as OrderDrawerDetails, so a refresh under the order drawer shares one request.
  const warehouseOrdersQuery = useMemo(
    () =>
      new PaginatedFilterQuery({
        page: 1,
        pageSize: 25,
        sortByColumn: "orderDate",
        isDescending: true,
        searchString: target?.salesId,
      }),
    [target?.salesId],
  );
  const { data, isLoading, isError, isPreviousData } =
    useGetWarehouseOrders(warehouseOrdersQuery);

  const details = useMemo(
    () =>
      target
        ? data?.items?.find((item) =>
            matchesIntegrationTransferRouteId(item, target),
          )
        : undefined,
    [data?.items, target],
  );

  // A query without the permission stays disabled, and a disabled v4 query reports loading.
  // The query keeps the previous search's results while it fetches, so ignore those.
  const isUnresolvable =
    open &&
    !details &&
    (!target ||
      !canReadWarehouseOrders ||
      isError ||
      (!isLoading && !isPreviousData && !!data));

  useRouteNotFoundToast(
    isUnresolvable ? routeId : null,
    INTEGRATION_TRANSFER_NOT_FOUND_MESSAGE,
    onNotFound,
  );

  if (details) {
    return (
      <ReadyDrawerBase path={open ? levelPath : null}>
        <StackedIntegrationCenterTransferIntegrationDrawer
          open={open}
          onClose={onClose}
          onCloseAll={onCloseAll}
          zIndex={zIndex}
          depth={depth}
          details={details}
        />
      </ReadyDrawerBase>
    );
  }
  if (isUnresolvable) {
    return null;
  }
  return (
    <RoutedDrawerLoading
      open={open}
      onClose={onClose}
      onCloseAll={onCloseAll}
      zIndex={zIndex}
      depth={depth}
      label="Loading Integration Center details"
      paperSx={{ width: { xs: "100%", sm: "50vw" } }}
    />
  );
}
